// ==========================================
// STEP 12: Create components/features/Feed/Feed.js
// ==========================================
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { usePosts } from '../../../hooks/usePosts';
import { useAuth } from '../../../hooks/useAuth';
import CreatePost from './CreatePost';
import Post from './Post';

const Feed = () => {
  const { user } = useAuth();
  const location = useLocation();
  
  // Determine category based on current route
  const getCategoryFromPath = (pathname) => {
    if (pathname === '/missing') return 'missing';
    if (pathname === '/found') return 'found';
    if (pathname === '/success') return 'success_stories';
    return 'feed';
  };

  const [currentCategory, setCurrentCategory] = useState(getCategoryFromPath(location.pathname));
  const { posts, loading, loadPosts, deletePost } = usePosts(currentCategory);

  // Update category when route changes
  useEffect(() => {
    const newCategory = getCategoryFromPath(location.pathname);
    setCurrentCategory(newCategory);
    loadPosts(newCategory);
  }, [location.pathname, loadPosts]);

  const handlePostCreated = () => {
    loadPosts(currentCategory);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Get page title based on category
  const getPageTitle = () => {
    if (currentCategory === 'missing') return '🔴 Missing Pets';
    if (currentCategory === 'found') return '🟢 Found Pets';
    if (currentCategory === 'success_stories') return '⭐ Success Stories';
    return '🏠 Home Feed';
  };

  // CHANGE: Line 17 - Add responsive max-width and remove hard-coded width
  return (
    <div className="w-full">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl shadow-lg p-6 mb-6">
        <h1 className="text-3xl font-bold">{getPageTitle()}</h1>
        <p className="text-sm opacity-90 mt-2">
          {currentCategory === 'missing' && 'Help reunite lost pets with their families'}
          {currentCategory === 'found' && 'Report pets you have found'}
          {currentCategory === 'success_stories' && 'Celebrate happy reunions'}
          {currentCategory === 'feed' && 'Share and discover pet stories'}
        </p>
      </div>

      <CreatePost onPostCreated={handlePostCreated} currentCategory={currentCategory} />
      
      {/* CHANGE: Line 19-24 - Add responsive grid (1 col mobile, 2 tablet, 3+ desktop) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-6 mb-16 md:mb-0">
        {posts.length === 0 ? (
          <div className="col-span-1 md:col-span-2 lg:col-span-3 text-center py-12 text-gray-500 bg-white rounded-xl shadow-lg">
            <p className="text-2xl mb-2">
              {currentCategory === 'missing' && '🔴'}
              {currentCategory === 'found' && '🟢'}
              {currentCategory === 'success_stories' && '⭐'}
              {currentCategory === 'feed' && '🏠'}
            </p>
            <p className="text-xl font-semibold">No posts in {getPageTitle()}</p>
            <p className="text-sm">Be the first to share something!</p>
          </div>
        ) : (
          posts.map((post) => (
            <Post
              key={post.id}
              post={post}
              currentUser={user}
              onDelete={deletePost}
              onUpdate={handlePostCreated}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default Feed;